import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import FileDrop from "@/components/admin/FileDrop";
import { Sparkles, RotateCcw, Save, Globe, Search, Loader2 } from "lucide-react";

const TEXT_FIELDS = [
  { key: "title", label: "Google / Page Title", max: 60, area: false },
  { key: "meta_description", label: "Meta Description", max: 160, area: true },
  { key: "og_title", label: "Link Preview Title", max: 65, area: false },
  { key: "og_description", label: "Link Preview Description", max: 200, area: true },
];

function Char({ v, max }) {
  const n = (v || "").length;
  return <span className={`text-[11px] ${n > max ? "text-red-500" : "text-slate-400"}`}>{n}/{max}</span>;
}

export default function SeoAdmin() {
  const [items, setItems] = useState([]);
  const [siteUrl, setSiteUrl] = useState("");
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(false);
  const [bulk, setBulk] = useState(false);

  const load = () => api.get("/admin/seo").then(({ data }) => {
    setItems(data.items || []);
    setSiteUrl(data.site_url || "");
  }).catch((e) => toast.error(apiError(e.response?.data?.detail)));

  useEffect(() => { load(); }, []);

  const open = (it) => {
    setActive(it.route);
    setForm({
      route: it.route, entity_type: it.entity_type, entity_key: it.entity_key, label: it.label,
      title: it.title, meta_description: it.meta_description, og_title: it.og_title,
      og_description: it.og_description, og_image: it.og_image, canonical: it.canonical,
      noindex: it.noindex, ai_suggestions: it.ai_suggestions || {}, manual_fields: it.manual_fields || [],
      default_title: it.default_title, default_description: it.default_description,
      resolved_canonical: it.resolved_canonical, has_record: it.has_record,
    });
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/seo", form);
      toast.success("SEO metadata saved");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setGen(true);
    try {
      const { data } = await api.post("/admin/seo/generate", {
        route: form.route, entity_type: form.entity_type, entity_key: form.entity_key,
      });
      setForm((f) => ({
        ...f, title: data.title, meta_description: data.meta_description,
        og_title: data.og_title, og_description: data.og_description,
        ai_suggestions: data.ai_suggestions || {}, manual_fields: [],
      }));
      toast.success("AI generated new suggestions");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  const restore = async (field) => {
    if (!form.has_record) {
      const s = form.ai_suggestions || {};
      if (field === "all") setForm((f) => ({ ...f, ...s }));
      else setForm((f) => ({ ...f, [field]: s[field] || f[field] }));
      return;
    }
    try {
      const { data } = await api.post("/admin/seo/restore", { route: form.route, field });
      setForm((f) => ({ ...f, title: data.title, meta_description: data.meta_description,
        og_title: data.og_title, og_description: data.og_description, manual_fields: data.manual_fields || [] }));
      toast.success("Restored AI suggestion");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const generateMissing = async () => {
    setBulk(true);
    const t = toast.loading("Generating SEO for all pages missing it… this can take a moment.");
    try {
      const { data } = await api.post("/admin/seo/generate-missing");
      toast.success(`Generated SEO for ${data.generated} page(s)${data.failed ? `, ${data.failed} failed` : ""}.`, { id: t });
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail), { id: t }); }
    finally { setBulk(false); }
  };

  const groups = items.reduce((acc, it) => { (acc[it.group] = acc[it.group] || []).push(it); return acc; }, {});
  const filt = (list) => list.filter((it) => !q || it.label.toLowerCase().includes(q.toLowerCase()) || it.route.includes(q.toLowerCase()));
  const hasAi = form && Object.keys(form.ai_suggestions || {}).length > 0;

  return (
    <div className="p-6 lg:p-8" data-testid="seo-admin">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-npst-ink">SEO & Link Preview Manager</h1>
          <p className="text-sm text-slate-500 mt-1">AI-generated titles, descriptions and social previews for every page. Edit anything — manual changes are never overwritten by AI.</p>
          <div className="mt-2 inline-flex items-center gap-2 text-[12px] text-slate-500 bg-slate-100 px-3 py-1 rounded-full"><Globe size={13} /> Canonical base: <span className="font-mono text-npst-navy">{siteUrl}</span></div>
        </div>
        <Button onClick={generateMissing} disabled={bulk} data-testid="seo-generate-missing" className="gap-2 bg-npst-gold hover:bg-npst-gold/90 text-npst-navyDeep font-semibold">
          {bulk ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate for all missing
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* LIST */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input placeholder="Search pages…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="seo-search" />
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {Object.keys(groups).map((g) => filt(groups[g]).length > 0 && (
              <div key={g}>
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 bg-slate-50 sticky top-0">{g}</div>
                {filt(groups[g]).map((it) => (
                  <button key={it.route} onClick={() => open(it)} data-testid={`seo-item-${it.route}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-npst-goldLight/30 transition-colors ${active === it.route ? "bg-npst-goldLight/40" : ""}`}>
                    <div className="text-[13px] font-medium text-npst-ink truncate">{it.label}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-slate-400 font-mono truncate">{it.route}</span>
                      {it.has_record ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">SET</span>
                        : <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">DEFAULT</span>}
                      {it.noindex && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">NOINDEX</span>}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* EDITOR */}
        <div className="bg-white rounded-lg border border-slate-200 p-5 lg:p-6">
          {!form ? (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-slate-400">
              <Sparkles size={30} className="mb-3 text-npst-gold" />
              <p className="text-sm">Select a page to manage its SEO & link preview.</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="seo-editor">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-npst-gold font-semibold">{form.entity_type}</div>
                  <div className="font-serif text-xl text-npst-ink">{form.label}</div>
                  <div className="text-[12px] text-slate-400 font-mono">{form.resolved_canonical}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={regenerate} disabled={gen} data-testid="seo-regenerate" className="gap-2">
                    {gen ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Regenerate with AI
                  </Button>
                  <Button onClick={save} disabled={busy} data-testid="seo-save" className="gap-2 bg-npst-navy hover:bg-npst-navyDeep">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
                  </Button>
                </div>
              </div>

              {/* Google preview */}
              <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Google preview</div>
                <div className="text-[#1a0dab] text-[16px] leading-tight truncate">{form.title || form.default_title}</div>
                <div className="text-[#006621] text-[12px]">{form.resolved_canonical}</div>
                <div className="text-[#545454] text-[12px] leading-snug line-clamp-2">{form.meta_description || form.default_description}</div>
              </div>

              {TEXT_FIELDS.map((f) => {
                const ai = form.ai_suggestions?.[f.key];
                const isManual = (form.manual_fields || []).includes(f.key);
                return (
                  <div key={f.key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[13px] font-semibold text-npst-ink flex items-center gap-2">
                        {f.label}
                        {isManual && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">MANUAL</span>}
                      </label>
                      <div className="flex items-center gap-2">
                        <Char v={form[f.key]} max={f.max} />
                        {ai && <button onClick={() => restore(f.key)} title="Restore AI suggestion" className="text-[11px] text-npst-navy hover:text-npst-gold inline-flex items-center gap-1" data-testid={`seo-restore-${f.key}`}><RotateCcw size={11} /> AI</button>}
                      </div>
                    </div>
                    {f.area
                      ? <Textarea rows={2} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={form[`default_${f.key === "meta_description" ? "description" : f.key === "title" ? "title" : ""}`] || ""} data-testid={`seo-field-${f.key}`} />
                      : <Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`seo-field-${f.key}`} />}
                    {ai && ai !== form[f.key] && <div className="text-[11px] text-slate-400 mt-1 truncate">AI: {ai}</div>}
                  </div>
                );
              })}

              <div>
                <label className="text-[13px] font-semibold text-npst-ink block mb-1">Link Preview Image</label>
                <p className="text-[12px] text-slate-400 mb-2">Shown on WhatsApp, Facebook, X and LinkedIn. Defaults to the NPST logo if left empty.</p>
                <FileDrop value={form.og_image} onChange={(v) => set("og_image", v)} label="Drag & drop a preview image (1200×630 recommended)" />
              </div>

              <div>
                <label className="text-[13px] font-semibold text-npst-ink block mb-1">Canonical URL override</label>
                <Input value={form.canonical || ""} onChange={(e) => set("canonical", e.target.value)} placeholder={form.resolved_canonical} data-testid="seo-field-canonical" />
              </div>

              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div>
                  <div className="text-[13px] font-semibold text-npst-ink">Hide from search engines (noindex)</div>
                  <div className="text-[12px] text-slate-400">Prevents Google from indexing this page.</div>
                </div>
                <Switch checked={!!form.noindex} onCheckedChange={(v) => set("noindex", v)} data-testid="seo-noindex" />
              </div>

              {hasAi && <button onClick={() => restore("all")} className="text-[12px] text-npst-navy hover:text-npst-gold inline-flex items-center gap-1" data-testid="seo-restore-all"><RotateCcw size={12} /> Restore all AI suggestions</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
